import React, { useState, useRef, useEffect } from 'react';
import { accessorialCodeAPI } from '../../api';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { toast } from 'sonner';

// Autocomplete input for accessorial descriptions backed by the code catalog.
// Shows CODE on top, name below. Offers "Add new" when no exact match.
export const AccessorialDescInput = ({ value, onSelect, testId }) => {
  const [text, setText] = useState(value || '');
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [addModal, setAddModal] = useState({ open: false, name: '', typicalUse: '' });
  const wrapRef = useRef(null);
  const debounceRef = useRef(null);
  const justPickedRef = useRef(false);

  useEffect(() => { setText(value || ''); }, [value]);

  useEffect(() => {
    const onDocClick = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  const doSearch = (q) => {
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      try {
        const res = await accessorialCodeAPI.search(q);
        setResults(res.data || []);
        setOpen(true);
      } catch (e) { console.debug('Accessorial search failed:', e?.message); }
    }, 250);
  };

  const handleChange = (e) => {
    const v = e.target.value;
    setText(v);
    if (v.trim().length >= 1) doSearch(v.trim());
    else { setResults([]); setOpen(false); }
  };

  const pick = (code) => {
    const label = `${code.code} - ${code.name}`;
    justPickedRef.current = true;
    setText(label);
    onSelect(label, code);
    setOpen(false);
    setTimeout(() => { justPickedRef.current = false; }, 300);
  };

  const handleAddNew = async () => {
    if (!addModal.name.trim()) { toast.error('Name is required'); return; }
    try {
      const res = await accessorialCodeAPI.create({ name: addModal.name.trim(), typical_use: addModal.typicalUse.trim() });
      toast.success(`Code ${res.data.code} created`);
      setAddModal({ open: false, name: '', typicalUse: '' });
      pick(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to create code');
    }
  };

  return (
    <div ref={wrapRef} style={{ position: 'relative', width: '100%' }}>
      <input
        className="lw-input"
        style={{ height: 24, fontSize: 12 }}
        value={text}
        onChange={handleChange}
        onFocus={() => { if (text.trim()) doSearch(text.trim()); }}
        onBlur={() => { if (!justPickedRef.current) onSelect(text, null); }}
        placeholder="Type to search codes..."
        data-testid={testId}
      />
      {open && (
        <div style={{
          position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 60,
          background: '#fff', border: '1px solid var(--lw-border-strong)', borderRadius: 4,
          boxShadow: '0 4px 14px rgba(0,0,0,0.12)', maxHeight: 220, overflowY: 'auto',
        }} data-testid={`${testId}-dropdown`}>
          {results.map(code => (
            <div
              key={code.id}
              onMouseDown={(e) => { e.preventDefault(); pick(code); }}
              style={{ padding: '5px 10px', cursor: 'pointer', borderBottom: '1px solid var(--lw-border)' }}
              onMouseEnter={(e) => { e.currentTarget.style.background = '#f1f3fa'; }}
              onMouseLeave={(e) => { e.currentTarget.style.background = '#fff'; }}
              data-testid={`acc-code-option-${code.code}`}
            >
              <div style={{ fontSize: 12, fontWeight: 800, color: 'var(--lw-primary)' }}>{code.code}</div>
              <div style={{ fontSize: 11, color: 'var(--lw-muted)' }}>{code.name}</div>
            </div>
          ))}
          <div
            onMouseDown={(e) => { e.preventDefault(); setAddModal({ open: true, name: text, typicalUse: '' }); setOpen(false); }}
            style={{ padding: '6px 10px', cursor: 'pointer', fontSize: 12, fontWeight: 700, color: 'var(--lw-link)', textDecoration: 'underline' }}
            data-testid="acc-code-add-new"
          >
            + Add new
          </div>
        </div>
      )}

      <Dialog open={addModal.open} onOpenChange={(o) => setAddModal(prev => ({ ...prev, open: o }))}>
        <DialogContent className="max-w-sm" data-testid="acc-code-add-modal">
          <DialogHeader>
            <DialogTitle>New Accessorial Code</DialogTitle>
            <DialogDescription>A unique 4-letter code will be generated automatically</DialogDescription>
          </DialogHeader>
          <div className="lw-root" style={{ display: 'flex', flexDirection: 'column', gap: 8, background: 'transparent' }}>
            <div>
              <span className="lw-label">Accessorial</span>
              <input
                className="lw-input"
                style={{ marginTop: 3 }}
                value={addModal.name}
                onChange={(e) => setAddModal(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Name of the accessorial"
                data-testid="acc-code-new-name"
              />
            </div>
            <div>
              <span className="lw-label">Typical use</span>
              <input
                className="lw-input"
                style={{ marginTop: 3 }}
                value={addModal.typicalUse}
                onChange={(e) => setAddModal(prev => ({ ...prev, typicalUse: e.target.value }))}
                placeholder="Brief description"
                data-testid="acc-code-new-use"
              />
            </div>
            <button className="lw-btn primary" style={{ height: 30 }} onClick={handleAddNew} data-testid="acc-code-new-save">
              Create Code
            </button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};
